import { useEffect, useState } from 'react';
import { userApi } from '../services/api';
import { pollCheckoutStatus, type BillingReturn, type CheckoutProgress } from '../billing/checkout-return';

export function useBillingReturn(returnTo: BillingReturn, userId: string | undefined, getToken: () => Promise<string | null>, refresh: (token: string, canCommit: () => boolean) => Promise<void>) {
  type Progress = CheckoutProgress | 'cancelled' | 'portal' | null;
  const [state, setState] = useState<{ userId: string | undefined; progress: Progress }>({ userId: undefined, progress: null });
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const setProgress = (progress: Progress) => setState({ userId, progress });
    if (!returnTo || !userId) { setProgress(null); return; }
    if (returnTo.kind !== 'success') { setProgress(returnTo.kind); return; }
    const abort = new AbortController();
    let verifiedToken: string | null = null;
    void pollCheckoutStatus({
      signal: abort.signal,
      check: async signal => {
        const token = await getToken();
        if (!token || signal.aborted) throw new Error('Authentication required');
        const response = await userApi.getCheckoutStatus(returnTo.sessionId, token, signal);
        if (!response.success) throw new Error('Billing status unavailable');
        verifiedToken = token;
        return response.status;
      },
      onProgress: setProgress,
      onActive: async () => {
        if (verifiedToken && !abort.signal.aborted) await refresh(verifiedToken, () => !abort.signal.aborted);
      },
    });
    return () => abort.abort();
  }, [returnTo, userId, getToken, refresh, generation]);
  return { progress: state.userId === userId ? state.progress : null, retry: () => setGeneration(value => value + 1) };
}
